import { useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useState } from 'react';
import { ApiErrorCode, getResponseErrorMessage } from '../core/api/error';
import { User } from '../core/auth/user';
import { executeSafe } from '../utils';
import { getUserName } from './index';
import { MUTATION_RESEND_USER_INVITATION } from './queries';

type ResendUserInvitationButtonWrapperProps = {
    user: User | Partial<User>;
    userInvitationId: string;
    renderButton: (
        working: boolean,
        resendUserInvitation: () => void
    ) => ReactElement;
    onSuccess?: () => void;
};

export const ResendUserInvitationButtonWrapper = ({
    user,
    userInvitationId,
    renderButton,
    onSuccess
}: ResendUserInvitationButtonWrapperProps) => {
    const { enqueueSnackbar } = useSnackbar();

    const [working, setWorking] = useState<boolean>(false);

    const [renewUserInvitation] = useMutation(MUTATION_RESEND_USER_INVITATION, {
        variables: {
            id: userInvitationId
        }
    });

    const userName = getUserName(user);

    const onClick = () => {
        setWorking(true);
        renewUserInvitation()
            .then(() => {
                enqueueSnackbar(
                    `Einladung an ${userName} wurde erneut versand`,
                    {
                        variant: 'success',
                        autoHideDuration: 5000
                    }
                );
                executeSafe(onSuccess);
            })
            .catch(e => {
                const message = getResponseErrorMessage(e, apiError => {
                    const code = apiError.getCode();

                    if (code === ApiErrorCode.USER_INACTIVE) {
                        return 'Das Nutzerkonto wurde zwischenzeitlich deaktiviert.';
                    }

                    return `Einladung konnte nicht erneut versendet werden. Fehler: "${e.message}"`;
                });

                enqueueSnackbar(message, {
                    variant: 'error',
                    autoHideDuration: 5000
                });
            })
            .finally(() => {
                setWorking(false);
            });
    };

    return <>{renderButton(working, onClick)}</>;
};
